import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import Accordion from '@nerdwallet/react-accordion';
import uuid from 'uuid/v4';
import { Box, CenteredContainer } from '@nerdwallet/currency';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { getFaqSchema } from '@nerdwallet/json-ld/mappings';
import classNames from 'classnames';
import getTextColor from '../../lib/text-color-config';
import UniversalText from '../universal-text/universal-text';
import {
  boolCheckboxSpec,
  repeatable,
  group,
  wrapper,
} from '../../lib/editing-configs';
import './universal-accordion.less';
import { updateComponent } from '../../containers/builder/actions';
import UniversalContainer from '../universal-container/universal-container';
import { AccordionIcon } from './assets/accordion-icon';
import { universalContainerPropTypes } from '../universal-container';

const defaultProps = {
  title: {
    text: 'Accordion title',
    groupButton: {
      bold: true,
      center: true,
    },
    margin: {
      currency: {
        bottom: '1rem',
      },
    },
  },
  subtitle: {
    text: 'This is subtitle',
    groupButton: {
      center: true,
    },
    margin: {
      currency: {
        bottom: '2.75rem',
      },
    },
  },
  panels: [
    {
      $unique_key: new Date().getTime(),
      header: {
        text: 'This is section 1',
      },
      body: {
        text: 'This is section 1 body',
      },
    },
    {
      $unique_key: new Date().getTime(),
      header: {
        text: 'This is section 2',
      },
      body: {
        text: 'This is section 2 body',
      },
    },
  ],
  disableDefaultProps: false,
};

const UniversalAccordion = (props) => {
  const {
    fullWidthMobile,
    strokeColorOptions,
    rollOverColorOptions,
    outputSchema,
    size,
    updateComponentProps,
    untouched,
    selectedComponent,
    disableDefaultProps,
    componentId,
    panels,
    container,
    title,
    subtitle,
    isCopy,
  } = props;
  const accordionRef = useRef();
  const accordionWithEmptyPropsRef = useRef();

  const nodeId = uuid();
  const [trkId] = useState(nodeId);

  const { textColor: strokeColorConfig } = getTextColor(strokeColorOptions);
  const { textColor: rollOverColorConfig } = getTextColor(rollOverColorOptions);
  const strokeColorStyle = { borderColor: strokeColorConfig };

  const mapped = panels?.map(({ header, body }) => ({
    answer: body,
    question: header,
  }));

  const faqSchema = getFaqSchema(mapped);
  const jsonLd = { '@context': 'http://schema.org', ...faqSchema };

  const elementImpressionProps = {
    elementContent: title,
    id: `universal-accordion-${trkId}`,
    elementDomType: 'div',
    elementName: 'Accordion',
    sectionCategory: 'UniversalAccordion',
  };

  const analytics = {
    enabled: true,
    payload: {
      elementName: 'landing_page_accordion_item',
    },
  };

  let mobileWidth;
  if (fullWidthMobile) {
    mobileWidth = '100%';
  } else {
    mobileWidth = '280px';
  }

  const containerWidthStyle = {
    width: size === 'sm' && `${mobileWidth}`,
  };

  const items = panels?.map(({ header, body }, key) => ({
    children: header ? (
      <UniversalText {...body} textType={header?.textType || 'text'} />
    ) : (
      ''
    ),
    title: header ? (
      <UniversalText {...header} textType={header?.textType || 'text'} />
    ) : (
      ''
    ),
    key,
  }));

  const fromGrid = selectedComponent?.type === 'Grid';
  const shouldShowFromGridPreview =
    fromGrid && !title && !subtitle && !panels.length;

  useEffect(() => {
    // because we want to apply different styling for each Accordion instance in the page
    if (accordionRef.current) {
      const accordionItems = accordionRef.current.querySelectorAll(
        '.editable-panel__wrapper .panel.panel-default'
      );

      const accordionFirstLine = accordionRef.current.querySelector(
        '.editable-panel__wrapper > div'
      );

      if (accordionFirstLine) {
        accordionFirstLine.style.borderColor = strokeColorConfig;
      }

      if (accordionItems.length) {
        accordionItems.forEach((node) => {
          // eslint-disable-next-line no-param-reassign
          node.style.borderColor = strokeColorConfig;
        });
      }

      const hoverNodes = accordionRef.current.querySelectorAll(
        '.editable-panel__wrapper .panel-group > div button'
      );

      if (hoverNodes.length && rollOverColorOptions) {
        accordionRef.current.classList.add('custom-accordion-style-color');
        hoverNodes.forEach((node) => {
          // eslint-disable-next-line no-param-reassign
          node.style.outlineColor = strokeColorConfig;
          // eslint-disable-next-line no-param-reassign
          accordionRef.current.style.setProperty(
            '--hover-background-color',
            rollOverColorConfig
          );
        });
      }
    }
  }, [rollOverColorConfig, strokeColorConfig, rollOverColorOptions]);

  // saves the very initial version of the UniversalAccordion
  // to be used when "disableDefaultProps" is activated
  useEffect(() => {
    if (untouched && !accordionWithEmptyPropsRef.current) {
      accordionWithEmptyPropsRef.current = selectedComponent;
    }
  }, [selectedComponent, untouched]);

  useEffect(() => {
    if (
      untouched &&
      selectedComponent?.id === componentId &&
      !disableDefaultProps &&
      !fromGrid &&
      !isCopy
    ) {
      updateComponentProps({
        ...selectedComponent,
        props: { ...defaultProps, untouched: false },
      });
    }
  }, [
    componentId,
    disableDefaultProps,
    selectedComponent,
    untouched,
    updateComponentProps,
    fromGrid,
    isCopy,
  ]);

  // If "disableDefaultProps" is active it load the very initial version of UniversalAccordion
  useEffect(() => {
    if (disableDefaultProps) {
      updateComponentProps({
        ...accordionWithEmptyPropsRef.current,
        props: {
          ...accordionWithEmptyPropsRef.current?.props,
          disableDefaultProps: true,
        },
      });
    }
  }, [disableDefaultProps, untouched, updateComponentProps]);

  if (shouldShowFromGridPreview) {
    return <AccordionIcon />;
  }

  return (
    <UniversalContainer {...container}>
      <CenteredContainer
        className="editable-panel__cont"
        style={containerWidthStyle}
      >
        <UniversalText {...title} />
        <UniversalText
          {...subtitle}
          defaultOptions={{ text: { textType: 'text' } }}
        />
        <ElementImpression {...elementImpressionProps}>
          <Box
            className={classNames(
              'editable-panel__wrapper',
              'custom-accordion-style-heading'
            )}
            style={{ ...strokeColorStyle }}
            ref={accordionRef}
          >
            <Accordion
              enableTracking
              items={items || []}
              analytics={analytics}
              id={nodeId}
            />
          </Box>
        </ElementImpression>
        {outputSchema && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          />
        )}
      </CenteredContainer>
    </UniversalContainer>
  );
};

UniversalAccordion.displayName = 'Accordion';
UniversalAccordion.getEditingConfig = (
  componentDocument,
  fieldLabel = 'Accordion',
  fieldName = '', // Since this field will be used when this component is wrapped by another component, let's leave it empty as default
  options = {
    disableGroup: true,
    disablePreview: false,
    includeContainer: false,
  }
) => {
  // Since this component can be used standalone or inside of another component
  // we'll use this function do dynamically get the name/path of each component
  // based in whether or not the fieldName is filled.
  const getInputName = (name) => {
    if (fieldName) {
      return `${fieldName}.${name}`;
    }
    return name;
  };

  const editorPanelOptions = [
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      `${getInputName('title')}`
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      `${getInputName('subtitle')}`,
      { text: { textType: 'text' } }
    ),
    group('Accordion settings', [
      ...getTextColor.getEditingConfig(
        componentDocument,
        `${getInputName('strokeColorOptions')}`,
        'Stroke color options'
      ),
      ...getTextColor.getEditingConfig(
        componentDocument,
        `${getInputName('rollOverColorOptions')}`,
        'Roll over color options'
      ),
      boolCheckboxSpec('fullWidthMobile', '100% width in mobile viewports?', {
        help: 'The accordion defaults to a width of 280px on mobile viewports. Check this box to make it 100% width for mobile viewports instead.',
      }),
      boolCheckboxSpec('outputSchema', 'Output as FAQpage JSON schema?', {
        help: 'Check this ONLY if 1) your content is a FAQ and 2) you want to output a FAQPage rich snippet for your FAQ. Please note that to enable enhanced search results, an editor must also un-check the "Prevent Search Engines from Following Links?" checkbox in the SEO Options section of the Wordpress editor.',
      }),
    ]),
    repeatable(
      `${getInputName('panels')}`,
      'Accordion Sections',
      'Section',
      (item) => [
        ...UniversalText.getEditingConfig(
          componentDocument,
          'Header',
          [
            'fontWeight',
            'groupButton',
            'margin',
            'padding',
            'fontFamily',
            'textColor',
            'textSize',
            'textType',
          ],
          `${getInputName(`panels.${item}.header`)}`,
          { text: { textType: 'text' } }
        ),
        ...UniversalText.getEditingConfig(
          componentDocument,
          'Body',
          [
            'fontWeight',
            'groupButton',
            'margin',
            'padding',
            'fontFamily',
            'textColor',
            'textSize',
            'textType',
          ],
          `${getInputName(`panels.${item}.body`)}`,
          { text: { textType: 'text' } }
        ),
      ]
    ),
  ];

  if (!options.disablePreview) {
    editorPanelOptions.unshift(
      boolCheckboxSpec(`disableDefaultProps`, 'Disable Default Properties?'),
      ...UniversalContainer.getEditingConfig(
        componentDocument,
        'Container',
        [
          'backgroundColor',
          'backgroundImage',
          'border',
          'margin',
          'padding',
          'hide',
          'jumpLinkId',
        ],
        `${getInputName('container')}`
      )
    );
  }

  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};

UniversalAccordion.defaultProps = {
  fullWidthMobile: false,
  panels: [],
  titleSize: '2',
  size: '',
  disableDefaultProps: false,
};

UniversalAccordion.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  universalAccordion: PropTypes.object,
  strokeColorOptions: PropTypes.object,
  rollOverColorOptions: PropTypes.object,
  fullWidthMobile: PropTypes.bool,
  panels: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      body: PropTypes.string,
    })
  ),
  size: PropTypes.string,
  outputSchema: PropTypes.bool,
  disableDefaultProps: PropTypes.bool,
  selectedComponent: PropTypes.object,
  componentId: PropTypes.string,
  untouched: PropTypes.bool,
  updateComponentProps: PropTypes.func,
  container: universalContainerPropTypes,
  isCopy: PropTypes.bool,
};

const mapDispatchToProps = (dispatch) => ({
  updateComponentProps: (componentData) => {
    dispatch(updateComponent(componentData));
  },
});

export default connect(
  createStructuredSelector({
    selectedComponent: ({ builder }) =>
      builder?.flatOutline?.[builder?.selected], // get Editor Panel current component data
  }),
  mapDispatchToProps
)(UniversalAccordion);
